import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send } from "lucide-react";

/**
 * Segmented HTML email broadcast (Resend).
 *
 * Segments: `all` / `pro` / `free` / `email_list` (paste addresses).
 * Plaintext is auto-wrapped in paragraph tags; raw HTML passes through.
 * Recipients with `newsletter_opt_in=false` are skipped automatically for
 * every segment except the hand-picked `email_list`.
 *
 * Extracted from Admin.jsx.
 */
export default function EmailBroadcastCard() {
  const [segment, setSegment] = useState("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) { toast.error("Subject + body required"); return; }
    const emailList = segment === "email_list"
      ? emails.split(/[\s,;\n]+/).map(s => s.trim()).filter(s => s.includes("@"))
      : undefined;
    if (segment === "email_list" && (!emailList || emailList.length === 0)) {
      toast.error("Paste at least one email address"); return;
    }
    if (!window.confirm(`Send this email to "${segment}"${emailList ? ` (${emailList.length} emails)` : ""}? Real inboxes will receive it.`)) return;
    setBusy(true);
    try {
      const isHtml = /<[a-z][\s\S]*>/i.test(body);
      const html = isHtml ? body : body.split(/\n{2,}/).map(p => `<p style="margin:0 0 12px;line-height:1.55;color:#333">${p.replace(/\n/g, "<br/>")}</p>`).join("");
      const wrapped = `<div style="padding:24px 32px;font-family:'Helvetica Neue',Arial,sans-serif;color:#111"><h2 style="margin:0 0 12px;font-size:20px">${subject}</h2>${html}</div>`;
      const r = await api.post("/admin/email-broadcast", { segment, subject, html: wrapped, emails: emailList });
      toast.success(`Email sent — ${r.data.sent}/${r.data.eligible_recipients} eligible (${r.data.candidates} candidates). ${r.data.failed} failed.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Broadcast failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-6" data-testid="email-broadcast-card">
      <CardContent className="p-6">
        <h2 className="serif text-2xl">Email broadcast</h2>
        <p className="text-sm text-muted-foreground mt-1">Segmented HTML email via Resend. Recipients with <code>newsletter_opt_in=false</code> are skipped automatically (except for a hand-picked email list). Plaintext is auto-wrapped in paragraphs — paste raw HTML for full control.</p>
        <form onSubmit={send} className="mt-4 space-y-3">
          <div>
            <Label>Segment</Label>
            <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={segment} onChange={(e) => setSegment(e.target.value)} data-testid="email-segment">
              <option value="all">All subscribed users</option>
              <option value="pro">Pro subscribers only</option>
              <option value="free">Free users only</option>
              <option value="email_list">Custom email list…</option>
            </select>
          </div>
          {segment === "email_list" && (
            <div>
              <Label>Emails (paste one per line, comma, or space)</Label>
              <Textarea rows={5} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="alice@example.com&#10;bob@example.com&#10;carol@example.com" data-testid="email-emails" className="mono text-xs" />
              <div className="mono text-[10px] text-muted-foreground mt-1">Only addresses that resolve to a DPP account will receive the email.</div>
            </div>
          )}
          <div>
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. New: AI Income Target Planner is live" data-testid="email-subject" />
          </div>
          <div>
            <Label>Body</Label>
            <Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Plaintext — double newline for a new paragraph.&#10;&#10;Or paste your own <p>HTML</p> for full control." data-testid="email-body" />
          </div>
          <Button type="submit" disabled={busy} data-testid="email-send" className="rounded-full">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send email</>}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
